import { useEffect, useState } from "react";
import { Button, Card, Cluster, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface PurgedOrg {
  org_id: string;
  display_name: string;
  slug: string;
  status: string;
  environment: string;
  purged_at: string | null;
  hard_delete_at: string | null;
}

export function RecycleBinPage() {
  const [orgs, setOrgs] = useState<PurgedOrg[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const row = await api.get<{ organizations: PurgedOrg[] }>("/operator/orgs/purged");
      setOrgs(row.organizations);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the recycle bin.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function restore(orgId: string) {
    setError(null);
    setRestoringId(orgId);
    try {
      await api.post(`/operator/orgs/${orgId}/restore`, {});
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Restore failed.");
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="Recycle bin"
        tip="Purged customers. Their CRM data was soft-deleted, not erased — Restore un-purges the tenant and its records. Config/child rows that purge hard-deletes cannot be recovered. Hard delete date is a 30-day target for reference only; no automated job enforces it yet."
      />
      {error && <Flash tone="error">{error}</Flash>}
      <Card>
        <table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Slug</th>
              <th>Environment</th>
              <th>Purged</th>
              <th>Hard delete date</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {orgs.map((org) => (
              <tr key={org.org_id}>
                <td>
                  {org.display_name}
                  <div className="admin-muted">{org.org_id}</div>
                </td>
                <td><code>{org.slug}</code></td>
                <td>{org.environment}</td>
                <td className="admin-readonly">{org.purged_at ? formatUtc(org.purged_at) : "—"}</td>
                <td className="admin-readonly">{org.hard_delete_at ? formatUtc(org.hard_delete_at) : "—"}</td>
                <td>
                  <Cluster gap={2}>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={restoringId === org.org_id}
                      onClick={() => restore(org.org_id)}
                    >
                      {restoringId === org.org_id ? "Restoring…" : "Restore"}
                    </Button>
                  </Cluster>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {orgs.length === 0 && <p className="admin-muted">The recycle bin is empty.</p>}
      </Card>
    </Stack>
  );
}
